/**
 * User login handler
 */
import { pushFunnelEvent } from '@utils/dataLayer';
import BaseHandler from './BaseHandler';
import DOMUtils from '../utils/DOMUtils';
import { FORM_SELECTORS, FIELD_SELECTORS } from '../constants';

class LoginHandler extends BaseHandler {
  // Selectors
  static SELECTORS = {
    ERROR_MESSAGE: '.auth-form__error-message--wrong-password',
    RESET_LINK: '.auth-form__link--reset',
  };

  /**
   * Handle login
   */
  async handleLogin(email, password) {
    const passwordInput = this.form.querySelector(FIELD_SELECTORS.userPass);

    await this.handleApiCall(
      FORM_SELECTORS.login,
      () => this.api.login(email, password),
      {
        showPreloader: true,
        successCallback: (data) => {
          // Track successful login
          pushFunnelEvent({
            section: 'Sign In',
            step: 4,
            label: 'Sign In - Success',
            method: 'Email',
          });

          const redirectUrl = this.getRedirectUrl();
          localStorage.removeItem('azds_sso_redirect');
          window.location.href = redirectUrl || data.redirect || window.location.href;
        },
        errorCallback: (data) => {
          this.handleLoginError(data, email, passwordInput);
        },
      },
    );
  }

  /**
   * Handle login errors
   */
  handleLoginError(data, email, passwordInput) {
    const errorMessage = this.getErrorMessage(data);

    // Track funnel error
    pushFunnelEvent({
      section: 'Sign In',
      step: 3,
      label: 'Sign In - Error',
      method: 'Email',
      errorMessage,
    });

    // If email is not verified, show verification step
    if (data.code === 'email_not_verified' && email) {
      this.stepNavigator.goToStep('verify-email');
      DOMUtils.setFieldValue(FIELD_SELECTORS.verifyEmailDisplay, email, this.form);
    } else if (data.code === 'auth_failed') {
      // Show special message with password recovery link
      this.showWrongPasswordError(passwordInput, email);
    } else {
      DOMUtils.showError(passwordInput, errorMessage);
    }
  }

  /**
   * Show wrong password error with recovery link
   */
  showWrongPasswordError(passwordInput, email) {
    const field = passwordInput.closest('.auth-form__field');
    const errorMessageEl = field?.querySelector(this.constructor.SELECTORS.ERROR_MESSAGE);
    const loginForm = this.form.querySelector(FORM_SELECTORS.login);
    const resetLink = loginForm?.querySelector(this.constructor.SELECTORS.RESET_LINK);

    if (!errorMessageEl) return;

    // Get message from data-messages
    const messageKey = errorMessageEl.dataset.messageKey || 'error_wrong_password';
    const message = this.messages.getMessage(messageKey);

    // Fill message (contains HTML, so use innerHTML)
    // eslint-disable-next-line no-param-reassign
    errorMessageEl.innerHTML = message;

    // Save email in data attribute for use when clicking link
    if (email) {
      // eslint-disable-next-line no-param-reassign
      errorMessageEl.dataset.userEmail = email;
    }

    // Show error message
    // eslint-disable-next-line no-param-reassign
    errorMessageEl.style.display = 'block';
    field.classList.add('auth-form__field--error');

    // Hide regular "Forgot password?" link
    if (resetLink) {
      // eslint-disable-next-line no-param-reassign
      resetLink.style.display = 'none';
    }

    // On input: hide error message and show link again
    passwordInput.addEventListener('input', () => {
      this.constructor.hideWrongPasswordError(errorMessageEl, field, resetLink);
    }, { once: true });
  }

  /**
   * Hide wrong password error and show reset link
   */
  static hideWrongPasswordError(errorMessageEl, field, resetLink) {
    // eslint-disable-next-line no-param-reassign
    errorMessageEl.style.display = 'none';
    field.classList.remove('auth-form__field--error');
    if (resetLink) {
      // eslint-disable-next-line no-param-reassign
      resetLink.style.display = '';
    }
  }
}

export default LoginHandler;
